/* The complete Whisper, read at the Keeper's own pace.
 * The supplied manuscript transcription is the only source of words.
 * Titles, edition subtitles and repeated printer's wordmarks are omitted here.
 */
(() => {
  'use strict';

  const source = window.VIULET_PRINTED_PIECE_TEXT;
  if (!source?.['manuscript-front'] || !source?.['manuscript-back']) return;
  // (The paragraphs arrive ready: mirror-era-printed-piece-text.js has already set the titles aside.)
  const paragraphs = [...source['manuscript-front'], ...source['manuscript-back']];
  const wordCount = text => text.trim().split(/\s+/u).length;

  // A stanza keeps its own line breaks, so a page is measured in rows as the screen shows them (about 38
  // characters each on a phone, where the Whisper is set large; about 92 on a wider screen): a page of short
  // verse lines and a page of prose then take the same room, and a page holds what the reading area can show
  // without scrolling (the same allowance it is given further down).
  const wide = typeof innerWidth === 'number' && innerWidth > 600;
  const allowance = Math.max(225, Math.min(590, (typeof innerHeight === 'number' ? innerHeight : 800) - 245));
  const ROW = wide ? 92 : 38, ROWS = Math.max(9, Math.min(16, Math.floor(allowance / (wide ? 23 : 32) * .9)));
  const GAP = .55, SPARE = 1.5;
  const rowsOf = line => Math.max(1, Math.ceil(line.length / ROW));
  const rowsIn = lines => lines.reduce((total, line) => total + rowsOf(line), 0);
  // Each page is a list of {text, tail}; tail marks the rest of a stanza that began on the page before.
  const sheets = [];
  let page = [], used = 0;
  const turn = () => { if (page.length) sheets.push(page); page = []; used = 0; };
  const put = (lines, tail) => { used += (page.length ? GAP : 0) + rowsIn(lines); page.push({text: lines.join('\n'), tail}); };
  paragraphs.forEach((text, index) => {
    let lines = text.split('\n'), tail = false;
    while (lines.length) {
      const room = ROWS - used - (page.length ? GAP : 0), need = rowsIn(lines);
      if (need <= room) {
        // A line that announces what follows is not left at the foot of a page.
        const next = paragraphs[index + 1];
        if (page.length && next && lines[lines.length - 1].endsWith(':')
            && need + GAP + rowsIn(next.split('\n').slice(0, 2)) > room) { turn(); continue; }
        put(lines, tail); break;
      }
      // A short stanza is never divided. It stays with its page when it runs over by a line or so, or when
      // that page would otherwise hold a line or two and nothing else; otherwise it waits for the next.
      if (lines.length <= 4 && page.length) {
        if ((need <= 2 && need <= room + SPARE) || used <= 2) { put(lines, tail); break; }
        turn(); continue;
      }
      // A longer one is divided between two of its lines: never one line alone on either side of the turn,
      // nor a page ending on a line that announces the next.
      // (At the very end, what goes over the turn is more than a signature.)
      const final = index === paragraphs.length - 1;
      const slight = rest => final && rest.length <= 2 && wordCount(rest.join(' ')) <= 6;
      let fit = 0, rows = 0;
      while (fit < lines.length && rows + rowsOf(lines[fit]) <= room) rows += rowsOf(lines[fit++]);
      while (fit > 0 && (lines.length - fit < 2 || lines[fit - 1].endsWith(':') || slight(lines.slice(fit)))) fit -= 1;
      if (fit >= 2) { put(lines.slice(0, fit), tail); lines = lines.slice(fit); tail = true; turn(); continue; }
      if (page.length) { turn(); continue; }
      // It opens a page and is longer than one: it takes what a page holds, and at least a line.
      let take = 0, held = 0;
      while (take < lines.length && (take === 0 || held + rowsOf(lines[take]) <= ROWS)) held += rowsOf(lines[take++]);
      if (lines.length - take === 1 && take > 2) take -= 1;
      put(lines.slice(0, take), tail); lines = lines.slice(take); tail = true;
      if (lines.length) turn();
    }
  });
  turn();
  // A signature is not left alone on the last page. It joins the page before it when that page has the room
  // (a line or so over is allowed); failing that, the stanza before it comes over with it, as long as the
  // page it leaves keeps enough to stand and does not end on a line that announces what follows.
  if (sheets.length > 1) {
    const last = sheets[sheets.length - 1], before = sheets[sheets.length - 2];
    const signed = last.map(item => item.text).join('\n').split('\n');
    const rowsOn = sheet => sheet.reduce((total, item, n) => total + (n ? GAP : 0) + rowsIn(item.text.split('\n')), 0);
    const join = (sheet, items) => items.forEach(item => {
      if (item.tail && sheet.length) { const head = sheet.pop(); sheet.push({text: head.text + '\n' + item.text, tail: head.tail}); }
      else sheet.push(item);
    });
    if (signed.length <= 2 && wordCount(signed.join(' ')) <= 6) {
      if (rowsOn(before) + GAP + rowsIn(signed) <= ROWS + SPARE) { join(before, last); sheets.pop(); }
      else {
        let keep = before.length - 1;
        while (keep > 1 && before[keep - 1].text.endsWith(':')) keep -= 1;
        const moved = before.slice(keep), stays = before.slice(0, keep);
        if (stays.length && rowsOn(stays) >= 3 && rowsOn(moved) + GAP + rowsIn(signed) <= ROWS) {
          const sheet = [...moved]; join(sheet, last);
          sheets.splice(sheets.length - 2, 2, stays, sheet);
        }
      }
    }
  }
  const pages = sheets.map(sheet => Object.freeze(sheet.map(item => item.text)));
  window.VIULET_WHISPER_CONTENT = Object.freeze({
    paragraphs: Object.freeze(paragraphs), pages: Object.freeze(pages),
    wordCount: paragraphs.reduce((total, text) => total + wordCount(text), 0)
  });
  if (typeof document === 'undefined') return;
  // A Mirror with no Whisper has nothing to read (its section stays hidden).
  if (!pages.length) return;

  const $ = selector => document.getElementById('viulet-v36').querySelector(selector);
  const toggle = $('#whisper-toggle'), panel = $('#full-whisper');
  const book = $('#whisper-pages'), position = $('#whisper-position');
  const previous = $('#whisper-previous'), next = $('#whisper-next');
  const progress = $('.reading-progress i');
  if (!toggle || !panel || !book || !position || !previous || !next) return;

  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const ease = 'cubic-bezier(.22,.61,.36,1)';
  let current = 0, requested = 0, revision = 0, animation = null, sizeFrame = 0;
  let expanded = false, measuredWidth = 0;

  // Emphasis changes the reading rhythm, never the manuscript's wording.
  const emphases = Array.isArray(window.MirrorEra?.emphases) ? window.MirrorEra.emphases : [];
  // A Mirror whose manuscript is not shown on the page: the last page closes the Whisper instead of opening it.
  const hasManuscript = !!window.MirrorEra?.manuscript;
  function makeParagraph(text, wordOffset) {
    const paragraph = document.createElement('p');
    paragraph.className = 'whisper-passage';
    let cursor = 0;
    const matches = emphases.map(([phrase, tag]) => ({phrase, tag, start: text.indexOf(phrase)}))
      .filter(match => match.start >= 0).sort((a, b) => a.start - b.start);
    matches.forEach(match => {
      if (match.start < cursor) return;
      paragraph.append(text.slice(cursor, match.start));
      const accent = document.createElement(match.tag);
      accent.textContent = match.phrase; paragraph.append(accent);
      cursor = match.start + match.phrase.length;
    });
    paragraph.append(text.slice(cursor));
    const walker = document.createTreeWalker(paragraph, NodeFilter.SHOW_TEXT), nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    let offset = wordOffset;
    nodes.forEach(node => {
      const fragment = document.createDocumentFragment();
      node.textContent.split(/(\s+)/u).forEach(token => {
        if (!token.trim()) fragment.append(token);
        else {
          const span = document.createElement('span');
          span.className = 'reading-word'; span.textContent = token;
          span.style.setProperty('--word-delay', `${Math.min(offset++ * 27, 2300)}ms`);
          fragment.append(span);
        }
      });
      node.replaceWith(fragment);
    });
    return paragraph;
  }

  const leaves = pages.map((texts, index) => {
    const leaf = document.createElement('div');
    leaf.className = 'whisper-leaf'; leaf.hidden = index !== current;
    let offset = 0;
    texts.forEach(text => {
      leaf.append(makeParagraph(text, offset)); offset += wordCount(text);
    });
    return leaf;
  });
  book.replaceChildren(...leaves);
  panel.classList.add('whisper-reader-v34');
  book.tabIndex = 0;
  book.setAttribute('role', 'region');
  position.setAttribute('aria-live', 'polite');
  position.setAttribute('aria-atomic', 'true');
  previous.type = 'button'; next.type = 'button'; toggle.type = 'button';

  function updateControls() {
    position.textContent = `${String(current + 1).padStart(2, '0')} / ${String(pages.length).padStart(2, '0')}`;
    book.setAttribute('aria-label', `The complete Whisper, page ${current + 1} of ${pages.length}`);
    previous.disabled = current === 0;
    previous.setAttribute('aria-label', 'Previous page of the Whisper');
    next.replaceChildren(current === pages.length - 1 ? (hasManuscript ? 'Read the manuscript ' : 'Close the Whisper ') : 'Continue ');
    const arrow = document.createElement('span');
    arrow.textContent = '→';
    arrow.setAttribute('aria-hidden', 'true'); next.append(arrow);
    next.setAttribute('aria-label', current === pages.length - 1 ? (hasManuscript ? 'Read the original manuscript' : 'Close the Whisper') : 'Next page of the Whisper');
    if (progress) progress.style.width = `${(current + 1) / pages.length * 100}%`;
  }

  function cancelTurn() {
    revision += 1; animation?.cancel(); animation = null;
    requested = current;
    book.removeAttribute('aria-busy');
  }

  function measureReadingHeight() {
    sizeFrame = 0;
    const style = getComputedStyle(book);
    const width = book.clientWidth - parseFloat(style.paddingLeft || 0) - parseFloat(style.paddingRight || 0);
    if (width <= 0) return;
    let tallest = 0;
    leaves.forEach(leaf => {
      const measure = leaf.cloneNode(true);
      measure.hidden = false; measure.classList.remove('is-reading');
      measure.classList.add('whisper-measure');
      measure.setAttribute('aria-hidden', 'true'); measure.style.width = `${width}px`;
      book.append(measure);
      tallest = Math.max(tallest, measure.getBoundingClientRect().height);
      measure.remove();
    });
    // Reserve the same area on every page; small phones can scroll the prose.
    const allowance = Math.max(225, Math.min(590, innerHeight - 245));
    const height = Math.min(allowance, Math.max(260, Math.ceil(tallest + 4)));
    book.style.setProperty('--whisper-reading-height', `${height}px`);
    measuredWidth = book.clientWidth;
  }
  function scheduleMeasure() {
    if (!sizeFrame) sizeFrame = requestAnimationFrame(measureReadingHeight);
  }

  function beginReading(leaf, stamp) {
    leaf.classList.remove('is-reading');
    requestAnimationFrame(() => {
      if (stamp === revision && expanded && !reduced.matches) leaf.classList.add('is-reading');
    });
  }

  function keepReadingInView() {
    const reading = panel.querySelector('.whisper-reading');
    const bounds = reading.getBoundingClientRect();
    if (bounds.top >= 18 && bounds.bottom <= innerHeight - 18) return;
    const top = Math.max(18, (innerHeight - bounds.height) / 2);
    scrollBy({top: bounds.top - top, behavior: reduced.matches ? 'instant' : 'smooth'});
  }

  async function turnPage(target) {
    target = Math.max(0, Math.min(pages.length - 1, target));
    if (!expanded || target === requested) return;
    requested = target;
    const stamp = ++revision;
    const opacity = getComputedStyle(book).opacity;
    animation?.cancel(); book.setAttribute('aria-busy', 'true');
    if (!reduced.matches) {
      animation = book.animate([{opacity}, {opacity: 0}], {duration: 260, easing: ease, fill: 'forwards'});
      await animation.finished.catch(() => {});
    }
    if (stamp !== revision || !expanded) return;
    current = target;
    leaves.forEach((leaf, index) => {
      leaf.hidden = index !== current; leaf.classList.remove('is-reading');
    });
    book.scrollTop = 0; updateControls();
    animation?.cancel(); animation = null;
    if (!reduced.matches) {
      animation = book.animate([{opacity: 0}, {opacity: 1}], {duration: 850, easing: ease});
    }
    beginReading(leaves[current], stamp);
    keepReadingInView();
    book.removeAttribute('aria-busy');
  }

  function setExpanded(open) {
    cancelTurn(); expanded = open;
    toggle.setAttribute('aria-expanded', String(open));
    toggle.firstElementChild.textContent = open ? 'Close the Whisper' : 'Read the full Whisper';
    panel.inert = !open; panel.setAttribute('aria-hidden', String(!open));
    panel.classList.toggle('is-open', open);
    leaves.forEach(leaf => leaf.classList.remove('is-reading'));
    if (open) {
      scheduleMeasure(); beginReading(leaves[current], revision);
      const stamp = revision;
      requestAnimationFrame(async () => {
        await Promise.all(panel.getAnimations().map(item => item.finished.catch(() => {})));
        if (expanded && stamp === revision) { measureReadingHeight(); keepReadingInView(); }
      });
    }
    updateControls();
    panel.dispatchEvent(new CustomEvent('viulet:whisper-state', {bubbles: true, detail: {open}}));
  }

  toggle.addEventListener('click', () => setExpanded(!expanded));
  previous.addEventListener('click', () => turnPage(requested - 1));
  next.addEventListener('click', () => {
    if (requested < pages.length - 1) turnPage(requested + 1);
    else if (current === pages.length - 1) {
      if (hasManuscript) next.dispatchEvent(new CustomEvent('viulet:read-manuscript', {
        bubbles: true, detail: {paper: 'manuscript', launcher: next}
      }));
      else { setExpanded(false); toggle.focus({preventScroll: true}); toggle.scrollIntoView({block: 'center', behavior: reduced.matches ? 'instant' : 'smooth'}); }
    }
  });
  book.addEventListener('keydown', event => {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    if (event.key === 'ArrowRight' && requested < pages.length - 1) {
      event.preventDefault(); turnPage(requested + 1);
    } else if (event.key === 'ArrowLeft' && requested > 0) {
      event.preventDefault(); turnPage(requested - 1);
    }
  });
  panel.addEventListener('keydown', event => {
    if (event.key === 'Escape' && expanded) {
      event.preventDefault(); setExpanded(false); toggle.focus({preventScroll: true});
    }
  });
  addEventListener('resize', scheduleMeasure, {passive: true});
  if ('ResizeObserver' in window) {
    new ResizeObserver(() => {
      if (Math.abs(book.clientWidth - measuredWidth) > .5) scheduleMeasure();
    }).observe(book);
  }
  document.fonts?.ready.then(scheduleMeasure);
  reduced.addEventListener('change', () => {
    cancelTurn(); leaves.forEach(leaf => leaf.classList.remove('is-reading'));
    if (expanded && !reduced.matches) beginReading(leaves[current], revision);
  });
  setExpanded(false);
  scheduleMeasure();
})();
